import { useQuery, type RefetchOptions } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { accountSummaryService } from "../services/accountSummaryService";
import type { IbkrPositionV2 } from "../types";
import { isValidPlutusUserId } from "../utils/plutusUserId";
import {
  AdminCustomerViewOwnershipError,
  emitAdminCustomerViewOwnerMismatch,
} from "../pages/admin/adminCustomerViewDiagnostics";

export interface AdminCustomerViewAccountQueryOptions {
  staleTime: number;
  gcTime: number;
  retry: false;
  retryOnMount: false;
  refetchOnMount: false;
  refetchOnWindowFocus: false;
  refetchOnReconnect: false;
  refetchInterval: false;
}

export interface AdminCustomerViewAccountRequest {
  targetUserId: string;
  enabled: boolean;
  maxAgeMs: number;
  queryOptions: AdminCustomerViewAccountQueryOptions;
}

export interface AdminCustomerViewAccountSummary {
  userId: string;
  netLiquidationSecurity: number;
  totalCashValueSecurity: number;
  grossPositionValueSecurity: number;
  buyingPower: number;
}

export interface CustomerViewOwnedAccountResult<TData> {
  data: TData | undefined;
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  loadedForUserId: string | null;
  dataUpdatedAt: number;
  refetch: () => Promise<void>;
}

export const ADMIN_CUSTOMER_ACCOUNT_MAX_AGE_MS = 15 * 60 * 1000;

export const ADMIN_CUSTOMER_ACCOUNT_QUERY_OPTIONS = {
  staleTime: 15 * 60 * 1000,
  gcTime: Number.POSITIVE_INFINITY,
  retry: false,
  retryOnMount: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchInterval: false,
} as const satisfies AdminCustomerViewAccountQueryOptions;

export const adminCustomerViewAccountQueryKeys = {
  positions: (targetUserId: string) =>
    ["account", "positionsV2", "admin-customer-view", targetUserId] as const,
  summary: (targetUserId: string) =>
    ["account", "summary", "admin-customer-view", targetUserId] as const,
};

const DECIMAL_STRING = /^-?\d+(?:\.\d+)?$/;

function decodeFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && DECIMAL_STRING.test(value)) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function decodeAdminCustomerViewAccountSummary(
  raw: unknown,
  expectedUserId: string
): AdminCustomerViewAccountSummary {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("Customer View could not verify this resource.");
  }
  const record = raw as Record<string, unknown>;
  if (record.userId !== expectedUserId) {
    emitAdminCustomerViewOwnerMismatch("summary");
    throw new AdminCustomerViewOwnershipError();
  }
  const netLiquidationSecurity = decodeFiniteNumber(record.netLiquidationSecurity);
  const totalCashValueSecurity = decodeFiniteNumber(record.totalCashValueSecurity);
  const grossPositionValueSecurity = decodeFiniteNumber(record.grossPositionValueSecurity);
  const buyingPower = decodeFiniteNumber(record.buyingPower);
  if (
    netLiquidationSecurity === null
    || totalCashValueSecurity === null
    || grossPositionValueSecurity === null
    || buyingPower === null
  ) {
    throw new Error("Customer View could not verify this resource.");
  }
  return {
    userId: expectedUserId,
    netLiquidationSecurity,
    totalCashValueSecurity,
    grossPositionValueSecurity,
    buyingPower,
  };
}

function toVisibleResult<T>(
  query: {
    data: T | undefined;
    error: Error | null;
    isLoading: boolean;
    isFetching: boolean;
    dataUpdatedAt: number;
  },
  effectiveEnabled: boolean,
  expectedUserId: string,
  refetch: () => Promise<void>
): CustomerViewOwnedAccountResult<T> {
  if (!effectiveEnabled) {
    return {
      data: undefined,
      error: null,
      isLoading: false,
      isFetching: false,
      loadedForUserId: null,
      dataUpdatedAt: 0,
      refetch: async () => undefined,
    };
  }
  const error = query.error;
  const hide = error !== null || query.data === undefined;
  const data = hide ? undefined : query.data;
  return {
    data,
    error,
    isLoading: query.isLoading && query.data === undefined,
    isFetching: query.isFetching,
    loadedForUserId: data === undefined ? null : expectedUserId,
    dataUpdatedAt: data === undefined ? 0 : query.dataUpdatedAt,
    refetch,
  };
}

function useSingleFlightRefetch(
  queryRefetch: (options?: RefetchOptions) => Promise<unknown>,
  effectiveEnabled: boolean
): () => Promise<void> {
  const refetchRef = useRef(queryRefetch);
  const inFlightRef = useRef<Promise<void> | null>(null);
  refetchRef.current = queryRefetch;

  return useCallback(() => {
    if (!effectiveEnabled) return Promise.resolve();
    if (inFlightRef.current) return inFlightRef.current;

    const request = Promise.resolve(
      refetchRef.current({ cancelRefetch: false })
    ).then(() => undefined);
    inFlightRef.current = request;
    const clear = () => {
      if (inFlightRef.current === request) {
        inFlightRef.current = null;
      }
    };
    void request.then(clear, clear);
    return request;
  }, [effectiveEnabled]);
}

function queryPolicy(options: AdminCustomerViewAccountQueryOptions) {
  return {
    staleTime: options.staleTime,
    gcTime: options.gcTime,
    retry: options.retry,
    retryOnMount: options.retryOnMount,
    refetchOnMount: options.refetchOnMount,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
    refetchOnReconnect: options.refetchOnReconnect,
    refetchInterval: options.refetchInterval,
  };
}

export function useAdminCustomerViewPositions(
  input: AdminCustomerViewAccountRequest
): CustomerViewOwnedAccountResult<IbkrPositionV2[]> {
  const effectiveEnabled = input.enabled === true && isValidPlutusUserId(input.targetUserId);
  const query = useQuery<IbkrPositionV2[], Error>({
    queryKey: adminCustomerViewAccountQueryKeys.positions(input.targetUserId),
    queryFn: async () => {
      const positions = await accountSummaryService.getAdminUserPositionsV2(
        input.targetUserId,
        input.maxAgeMs
      );
      if (!Array.isArray(positions) || positions.some((row) => row.userId !== input.targetUserId)) {
        emitAdminCustomerViewOwnerMismatch("positions");
        throw new AdminCustomerViewOwnershipError();
      }
      return positions;
    },
    enabled: effectiveEnabled,
    ...queryPolicy(input.queryOptions),
  });
  const refetch = useSingleFlightRefetch(query.refetch, effectiveEnabled);
  return toVisibleResult(query, effectiveEnabled, input.targetUserId, refetch);
}

export function useAdminCustomerViewAccountSummary(
  input: AdminCustomerViewAccountRequest
): CustomerViewOwnedAccountResult<AdminCustomerViewAccountSummary> {
  const effectiveEnabled = input.enabled === true && isValidPlutusUserId(input.targetUserId);
  const query = useQuery<AdminCustomerViewAccountSummary, Error>({
    queryKey: adminCustomerViewAccountQueryKeys.summary(input.targetUserId),
    queryFn: async () => {
      const raw = await accountSummaryService.getAdminUserAccountSummary(
        input.targetUserId,
        input.maxAgeMs
      );
      return decodeAdminCustomerViewAccountSummary(raw, input.targetUserId);
    },
    enabled: effectiveEnabled,
    ...queryPolicy(input.queryOptions),
  });
  const refetch = useSingleFlightRefetch(query.refetch, effectiveEnabled);
  return toVisibleResult(query, effectiveEnabled, input.targetUserId, refetch);
}
